import { BaseWidgetFormFields } from 'components/consumer-page-widgets/BaseWidgetFormFields';
import { Button, Typography } from 'ui';
import { Form, Formik } from 'formik';
import {
  RowLayoutWidgetFields,
  RowLayoutWidgetFormFields,
  validLayoutWidgetItemTypes,
  validateRowLayoutWidgetFormFields,
} from 'components/consumer-page-widgets/row-layout-widget/RowLayoutWidgetFormFields';
import { StoreType } from 'utils/connect';
import { consumer } from '@tock/coretypes';
import { fetchAllNamedConfigurations } from 'actions/ConsumerPageActions';
import {
  getBaseFormFieldsInitialValues,
  useUpdateNamedConfigurations,
} from 'components/consumer-page-widgets/widgetFormUtils';
import { useAppDispatch } from 'state/hooks';
import { useSelector } from 'react-redux';
import {
  widgetFormButtonContainer,
  widgetFormRoot,
} from 'components/consumer-page-widgets/widgetFormCssDefs';

import React, { FunctionComponent } from 'react';

interface UpdateRowLayoutWidgetFormProps {
  selectedWidgetToEdit?: consumer.NamedConfiguration;
  onCompleteOrCancel(): void;
}

function getInitialRowLayoutValues(
  items: consumer.IRowLayoutWidgetItemConfig[],
  selectedWidgetToEdit?: consumer.NamedConfiguration
): RowLayoutWidgetFields {
  return {
    ...getBaseFormFieldsInitialValues({ widgetToEdit: selectedWidgetToEdit }),
    namedConfigurationIdLeft: items?.[0].namedWidgetConfigurationId ?? undefined,
    widthPercentageLeft: items?.[0].widthPercentage ?? 50,
    namedConfigurationIdRight: items?.[1].namedWidgetConfigurationId ?? undefined,
    widthPercentageRight: items?.[1].widthPercentage ?? 50,
  };
}

export const UpdateRowLayoutWidgetForm: FunctionComponent<
  React.PropsWithChildren<UpdateRowLayoutWidgetFormProps>
> = ({ selectedWidgetToEdit, onCompleteOrCancel }) => {
  const updateNamedConfig = useUpdateNamedConfigurations();

  const dispatch = useAppDispatch();

  React.useEffect(() => {
    dispatch(fetchAllNamedConfigurations());
  }, [dispatch]);

  const activeNamedConfigs = useSelector(
    (state: StoreType) => state.consumerPage.namedConfigurations
  ) as consumer.NamedConfiguration[]; // TODO: Fix types on consumerPage state slice

  const namedConfigsFilteredForValidTypes = activeNamedConfigs.filter((config) => {
    return (
      config.namedConfigurationConfig &&
      validLayoutWidgetItemTypes.includes(config.namedConfigurationConfig)
    );
  });

  if (!selectedWidgetToEdit?.rowLayoutWidgetConfig) {
    return null;
  }

  const initialValues = getInitialRowLayoutValues(
    selectedWidgetToEdit.rowLayoutWidgetConfig.rowLayoutWidgetItemConfig ?? []
  );

  const handleSubmit = ({
    namedConfigurationIdLeft,
    widthPercentageLeft,
    namedConfigurationIdRight,
    widthPercentageRight,
  }: RowLayoutWidgetFields) => {
    return updateNamedConfig({
      ...selectedWidgetToEdit,
      rowLayoutWidgetConfig: {
        rowLayoutWidgetItemConfig: [
          {
            namedWidgetConfigurationId: namedConfigurationIdLeft,
            widthPercentage: widthPercentageLeft,
          },
          {
            namedWidgetConfigurationId: namedConfigurationIdRight,
            widthPercentage: widthPercentageRight,
          },
        ],
      },
    }).then(onCompleteOrCancel);
  };

  return (
    <>
      <Typography variant="heading1Bold">Editing widget: {selectedWidgetToEdit.name}</Typography>
      <Formik<RowLayoutWidgetFields>
        enableReinitialize
        initialValues={{
          ...initialValues,
        }}
        validate={validateRowLayoutWidgetFormFields}
        onSubmit={handleSubmit}
      >
        <Form css={widgetFormRoot}>
          <BaseWidgetFormFields />
          <RowLayoutWidgetFormFields namedConfigurations={namedConfigsFilteredForValidTypes} />
          <div css={widgetFormButtonContainer}>
            <Button type="submit">Submit</Button>
            <Button onClick={onCompleteOrCancel} type="button" variant="outlined">
              Cancel
            </Button>
          </div>
        </Form>
      </Formik>
    </>
  );
};
